import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import type { SisColumn, SisDistrict, SisField, SisValue } from './sisMatchingApi';
import { closestColumns, columnLine, dateReadLine, fieldPill, formatShortDate, ID_FIELDS, NOT_IN_SIS, TIERS } from './sisMatchingView';
import { SIS, sisText } from './sisTokens';
import { SisPill } from './SisPill';
import { tooManyNote, ValueTable } from './ValueTable';

type Props = {
  district: SisDistrict;
  field: SisField;
  onColumn: (field: SisField, column: string) => void;
  onNotInSis: (field: SisField) => void;
  onMeaning: (field: SisField, value: SisValue, meaning: string) => void;
};

export function FieldPanel({ district, field, onColumn, onNotInSis, onMeaning }: Props) {
  const [showAll, setShowAll] = useState(false);
  const tier = TIERS.find((t) => t.tier === field.tier)?.name ?? '';
  const chosen = field.choice.notInSis ? '' : (field.choice.column ?? '');
  const columns = new Map(district.columns.map((c) => [c.name, c]));
  const options = closestColumns(field.ranked, chosen, showAll)
    .map((r) => columns.get(r.column))
    .filter((c): c is SisColumn => c !== undefined)
    .filter((c) => !((field.kind === 'group' || field.kind === 'value') && c.identity === true));
  const column = columns.get(chosen);
  const taken = field.taken ?? {};
  return (
    <Box component="section" data-testid="sis-field-panel" aria-labelledby="sis-field-title" sx={{ flex: 1, minWidth: 0, padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px', bgcolor: SIS.surface, borderRadius: '8px', boxShadow: SIS.cardShadow }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
        <Box>
          <Typography component="h2" id="sis-field-title" sx={sisText.h4}>{field.label}</Typography>
          <Typography sx={{ ...sisText.body1, color: SIS.muted, marginTop: '4px' }}>{tier}</Typography>
        </Box>
        <SisPill pill={fieldPill(field)} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Typography sx={sisText.eyebrow}>{`Which column in the feed matches to ${field.label}?`}</Typography>
        {options.map((c) => {
          const holder = chosen === c.name ? undefined : taken[c.name];
          return (
            <Option key={c.name} name={c.name} lines={holder ? [columnLine(c)[0], `Used by ${holder}`] : columnLine(c)} on={chosen === c.name} disabled={holder !== undefined} onClick={() => onColumn(field, c.name)} />
          );
        })}
        {!ID_FIELDS.includes(field.key) && (
          <Option
            name={NOT_IN_SIS}
            lines={['', field.choice.notInSis && field.choice.at ? `${field.choice.byName || field.choice.by || ''} · ${formatShortDate(field.choice.at)}` : '']}
            on={field.choice.notInSis}
            wide
            onClick={() => onNotInSis(field)}
          />
        )}
        <Box
          component="button"
          type="button"
          onClick={() => setShowAll((s) => !s)}
          sx={{ ...sisText.body1, alignSelf: 'flex-start', border: 0, background: 'none', padding: 0, color: SIS.primary, fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
        >
          {showAll ? 'Show fewer columns' : `Show all ${district.columns.length} columns`}
        </Box>
      </Box>
      <Body district={district} field={field} column={column} onMeaning={onMeaning} />
    </Box>
  );
}

function Option({ name, lines, on, wide, disabled, onClick }: { name: string; lines: [string, string]; on: boolean; wide?: boolean; disabled?: boolean; onClick: () => void }) {
  return (
    <Box
      component="button"
      type="button"
      aria-pressed={on}
      disabled={disabled}
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: on ? '11px 15px' : '12px 16px',
        border: on ? `2px solid ${SIS.primary}` : `1px solid ${SIS.divider}`,
        borderRadius: '8px',
        bgcolor: on ? SIS.selectedBg : SIS.surface,
        textAlign: 'left',
        width: '100%',
        cursor: disabled ? 'default' : 'pointer',
        '&:hover': { borderColor: on ? SIS.primary : SIS.muted },
      }}
    >
      <Box
        component="span"
        aria-hidden="true"
        sx={{ width: 20, height: 20, flexShrink: 0, borderRadius: '9999px', border: `2px solid ${on ? SIS.primary : SIS.muted}`, display: 'flex', alignItems: 'center', justifyContent: 'center', '&::after': on ? { content: '""', width: 8, height: 8, borderRadius: '9999px', bgcolor: SIS.primary } : undefined }}
      />
      <Typography data-testid="sis-option-name" sx={{ ...sisText.body1Strong, width: wide ? 'auto' : 160, flex: wide ? 1 : undefined, flexShrink: 0 }}>{name}</Typography>
      {lines[0] && <Typography sx={{ ...sisText.body1, width: 200, flexShrink: 0, whiteSpace: 'nowrap' }}>{lines[0]}</Typography>}
      {lines[1] && <Typography sx={{ ...sisText.body1, color: SIS.muted, flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{lines[1]}</Typography>}
    </Box>
  );
}

function Body({ district, field, column, onMeaning }: { district: SisDistrict; field: SisField; column?: SisColumn; onMeaning: Props['onMeaning'] }) {
  if (field.choice.notInSis || !column || column.filled === 0) return null;
  if (field.kind === 'value') {
    if (field.state === 'toomany') {
      return (
        <Box sx={{ border: `1px solid ${SIS.divider}`, borderRadius: '8px' }}>
          <Typography sx={{ ...sisText.body1, color: SIS.muted, padding: '12px 16px' }}>{tooManyNote(field, column.distinct)}</Typography>
        </Box>
      );
    }
    return <ValueTable field={field} confirmedAt={district.confirmation?.at} onPick={(value, meaning) => onMeaning(field, value, meaning)} />;
  }
  if (field.kind === 'date') {
    const line = dateReadLine(column, field.key);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {line.ok ? <CheckCircleOutlineIcon aria-hidden="true" sx={{ width: 20, height: 20, color: SIS.success }} /> : <ErrorOutlineIcon aria-hidden="true" sx={{ width: 20, height: 20, color: SIS.warningDark }} />}
        <Typography sx={sisText.body1}>{line.text}</Typography>
      </Box>
    );
  }
  if (field.kind === 'group') {
    return <ValueTable field={field} onPick={() => {}} />;
  }
  return null;
}
